'use client';

import { motion } from "framer-motion";
import { Contact } from "@/components/Contact";
import { PageHero } from "@/components/shared/PageHero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    category: "Services & Process",
    questions: [
      {
        question: "What services do you offer?",
        answer: "We offer a comprehensive range of creative services including brand strategy, digital design, content creation, and digital marketing. Our services can be tailored to meet your specific needs, whether you&apos;re looking for a complete brand overhaul or specific creative solutions."
      },
      {
        question: "How long does a typical project take?",
        answer: "Project timelines vary depending on scope and complexity. A typical website design project takes 8-12 weeks, while a comprehensive branding project might take 12-16 weeks. We&apos;ll provide detailed timelines during our initial consultation."
      },
      {
        question: "Do you work with clients remotely?",
        answer: "Yes, we work with clients both locally and remotely. We use modern collaboration tools and maintain regular communication to ensure smooth project execution regardless of location."
      }
    ]
  },
  {
    category: "Pricing & Packages",
    questions: [
      {
        question: "How do you structure your pricing?",
        answer: "We offer both project-based and retainer pricing models. Each proposal is customized based on your specific needs, goals, and project scope. We&apos;ll provide detailed pricing during our consultation."
      },
      {
        question: "Do you offer payment plans?",
        answer: "Yes, we offer flexible payment plans for larger projects. Typically, this includes an initial deposit followed by milestone-based payments throughout the project."
      },
      {
        question: "What&apos;s included in your maintenance packages?",
        answer: "Our maintenance packages include regular updates, security monitoring, performance optimization, and content updates. We offer different tiers to suit various needs and budgets."
      }
    ]
  },
  {
    category: "Process & Collaboration",
    questions: [
      {
        question: "How do you handle revisions?",
        answer: "Our process includes designated revision rounds at key project milestones. We work closely with you to gather feedback and make necessary adjustments to ensure the final product meets your vision."
      },
      {
        question: "Who owns the rights to the final work?",
        answer: "Upon final payment, you receive full ownership rights to all final deliverables. We maintain the right to showcase the work in our portfolio unless otherwise agreed upon."
      },
      {
        question: "How do you ensure project success?",
        answer: "We follow a structured process that includes regular check-ins, clear communication, and defined milestones. Our project management approach ensures alignment with your goals throughout the project."
      }
    ]
  },
  {
    category: "Technical & Support",
    questions: [
      {
        question: "What platforms do you work with?",
        answer: "We work with various platforms including WordPress, Shopify, and custom solutions. Our recommendations are based on your specific needs, goals, and technical requirements."
      },
      {
        question: "Do you provide training and documentation?",
        answer: "Yes, we provide comprehensive training and documentation for all our digital products. This ensures your team can effectively manage and update content after launch."
      },
      {
        question: "What kind of support do you offer after launch?",
        answer: "We offer various post-launch support options, from basic maintenance to comprehensive growth packages. We&apos;ll help you choose the right support level for your needs."
      }
    ]
  }
];

export function FaqContent() {
  return (
    <main className="min-h-screen bg-white">
      <PageHero
        title="Get Answers"
        description="Your most common concerns about our services, process, and pricing answered in one place."
      />

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-16"
          >
            {faqs.map((category, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl md:text-3xl font-bold mb-8 uppercase tracking-wider" style={{ color: 'rgb(29,29,31)', fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', letterSpacing: '0.05em' }}>
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, faqIndex) => (
                    <AccordionItem
                      key={faqIndex}
                      value={`${index}-${faqIndex}`}
                      className="bg-white rounded-2xl border border-[#171A20]/10 hover:border-[#171A20]/20 transition-all duration-300 data-[state=open]:border-[#171A20]/20"
                    >
                      <AccordionTrigger className="px-8 md:px-8 py-8 md:py-8 text-lg md:text-xl font-semibold transition-colors duration-200" style={{ color: 'rgb(29,29,31)' }}>
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-8 md:px-8 pb-8 md:pb-8 text-base md:text-lg leading-relaxed" style={{ color: 'rgba(0,0,0,0.88)' }}>
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Contact />
    </main>
  );
} 